import PropTypes from 'prop-types';
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import Button from "../../../../components/UI/Button";
import {
    CheckIcon,
    DownloadIcon,
    PenIcon,
} from "../../../../Icons";
import { canUserSignGeneralForm } from "../../../../utils/formPermissions";
import InputDatePicker from "../../../EvaluationNew/components/InputDatePicker";
import EvaluationSignatureModal from "../../../EvaluationNew/components/EvaluationSignatureModal";

const GeneralFormFooter = ({
    methods,
    onSave,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName,
    employeeName,
    applicantSignature,
    isReadOnly = false,
    isSubmitting = false,
    applicantSignatureDateField = "applicantSignedAt",
    onSubmit
}) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);

    useEffect(() => {
        try {
            const userStr = localStorage.getItem("currentUser");
            if (userStr) {
                setCurrentUser(JSON.parse(userStr));
            }
        } catch (error) {
            console.error("Error parsing currentUser", error);
        }
    }, []);

    useEffect(() => {
        if (!isSubmitting) setPendingAction(null);
    }, [isSubmitting]);

    const canSignEmployee = canUserSignGeneralForm(currentUser, 'applicant');
    const isAdmin = canUserSignGeneralForm(currentUser, 'admin');

    const handleAction = async (type) => {
        if (isSubmitting || isReadOnly) return;
        setPendingAction(type);
        await methods.handleSubmit(async (data) => {
            if (type === 'save') {
                if (onSave) await onSave(data, 'save');
            } else if (type === 'completed') {
                if (onSave) await onSave(data, 'completed');
            }
        }, (errors) => {
            setPendingAction(null);
            const sigError = errors.applicantSignature || errors.reviewedBySignature || errors.patientSignature;
            if (sigError) {
                toast.error(sigError.message || "Signature is required");
            } else {
                toast.error("Please fix errors before proceeding.");
            }
        })();
    };

    const currentUserFullName = currentUser
        ? `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim() || currentUser.name || ""
        : "";

    // Employee: prefer logged-in name. Admin: show form employee name (editable above).
    const displayedEmployeeName = isAdmin
        ? (employeeName || "Enter Employee Name")
        : (currentUserFullName || employeeName || "Enter Employee Name");

    return (
        <div className="mt-12 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-gray-200 pt-8">
                <div className="flex flex-col h-full bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 pb-3 border-b border-gray-100 flex items-center gap-2 uppercase tracking-wide">
                        Employee Section
                    </h3>
                    <div className="space-y-5">
                        <div>
                            <p className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Employee Name</p>

                            <div className="p-3 bg-gray-50 border font-medium border-gray-200 rounded-lg min-h-[50px] text-gray-800 flex items-center">
                                {displayedEmployeeName}
                            </div>
                        </div>

                        <div>
                            <p className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Employee Signature</p>
                            {applicantSignature ? (
                                <div className="space-y-2">
                                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 font-medium">
                                        <p className="font-script">{applicantSignature}</p>
                                    </div>
                                    {canSignEmployee && !isReadOnly ? (
                                        <button
                                            type="button"
                                            onClick={() => openSignatureModal("applicant")}
                                            className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                                        >
                                            Re-sign
                                        </button>
                                    ) : null}
                                </div>
                            ) : canSignEmployee ? (
                                <button
                                    type="button"
                                    onClick={() => openSignatureModal("applicant")}
                                    className="w-full group px-4 py-3 bg-white text-blue-600 rounded-lg border-2 border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50 transition-all shadow-sm flex items-center justify-center gap-2"
                                    disabled={isReadOnly}
                                >
                                    <PenIcon className="h-5 w-5" />
                                    <span className="font-bold text-sm tracking-wide uppercase">Click to Sign</span>
                                </button>
                            ) : (
                                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg min-h-[50px] text-gray-400 italic flex items-center">
                                    Pending Signature
                                </div>
                            )}
                            {methods.formState.errors.applicantSignature && (
                                <p className="text-red-500 text-xs mt-1">{methods.formState.errors.applicantSignature.message}</p>
                            )}
                        </div>

                        <InputDatePicker
                            formatSeparator="/"
                            name={applicantSignatureDateField}
                            label="Date Signed"
                            disabled={isReadOnly || !canSignEmployee}
                            max={new Date().toISOString().split("T")[0]}
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap justify-end gap-4 pb-12 border-t border-gray-100 pt-8">
                <Button
                    text={pendingAction === "save" && isSubmitting ? "Saving…" : "Save"}
                    onClick={() => handleAction("save")}
                    icon={<DownloadIcon className="h-5 w-5" />}
                    disabled={isReadOnly || isSubmitting}
                    loading={pendingAction === "save" && isSubmitting}
                />
                <Button
                    text={pendingAction === "completed" && isSubmitting ? "Submitting…" : "Completed"}
                    onClick={() => handleAction("completed")}
                    icon={<CheckIcon className="h-5 w-5" />}
                    disabled={isReadOnly || isSubmitting}
                    loading={pendingAction === "completed" && isSubmitting}
                />
            </div>

            <EvaluationSignatureModal
                isOpen={isSignatureModalOpen}
                onClose={closeSignatureModal}
                onSign={handleSignatureApply}
                disableSign={!signerName}
            >
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Signer Name</label>
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg font-medium text-gray-800">
                        {signerName || "No name provided"}
                    </div>
                </div>
            </EvaluationSignatureModal>
        </div>
    );
};

GeneralFormFooter.propTypes = {
    methods: PropTypes.object.isRequired,
    onSave: PropTypes.func,
    onSubmit: PropTypes.func,
    onStatusUpdate: PropTypes.func,
    isSignatureModalOpen: PropTypes.bool.isRequired,
    openSignatureModal: PropTypes.func.isRequired,
    closeSignatureModal: PropTypes.func.isRequired,
    handleSignatureApply: PropTypes.func.isRequired,
    signerName: PropTypes.string.isRequired,
    employeeName: PropTypes.string,
    applicantSignature: PropTypes.string,
    isReadOnly: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    applicantSignatureDateField: PropTypes.string,
};

export default GeneralFormFooter;
